import { inject } from "@angular/core";
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { AuthToken } from "@company-box/models";
import { AUTH_SVC, AuthService } from "@company-box/services";
import { from, Observable, throwError } from "rxjs";
import { catchError, finalize, shareReplay, switchMap } from "rxjs/operators";

/**
 * Account-area 401 handler.
 *
 * Must be registered BEFORE `bearerTokenHeaderInterceptor` so that the replayed
 * request flows back through the bearer interceptor and picks up the refreshed
 * Authorization header.
 */

// Coalesces concurrent 401s (several account API calls in flight) onto a single
// refresh — important because the OAuth server may rotate the refresh token, so
// firing renewToken() per request would invalidate the others.
let refresh$: Observable<AuthToken> | null = null;

export const accountUnauthorizedInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AUTH_SVC) as AuthService;
  const logger = inject(NGXLogger);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        onAccountPage(router.url) &&
        // The refresh call itself lives at /oauth/token; never recurse on it.
        !req.url.includes("/oauth/token")
      ) {
        return redirectToLogin(error, router, authService, logger);
      }
      return throwError(() => error);
    })
  );
};

const redirectToLogin = (
  error: HttpErrorResponse,
  router: Router,
  authService: AuthService,
  logger: NGXLogger
): Observable<never> => {
  logger.debug("[accountUnauthorizedInterceptor] token refresh failed; redirecting to /login");
  authService.clearToken();
  void router.navigate(["/login"]);
  return throwError(() => error);
};

// Matches the /account area exactly (e.g. "/account", "/account/update-account")
// while ignoring query/fragment and not matching look-alikes like "/accounting".
const onAccountPage = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return path === "/account" || path.startsWith("/account/") || path === "/continue-project";
};
